import { useRef } from "react";
import { fileUrl, formatWhen } from "@/lib/api";

const RichNote = ({ text }) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const Lines = ({ text }) =>
  text.split("\n").map((l, i, arr) => (
    <span key={i} style={{ display: "inline" }}>{l}{i < arr.length - 1 && <br />}</span>
  ));

const Facts = ({ event }) => {
  const [l1, l2] = formatWhen(event.start, event.end);
  const facts = [
    { k: "When", v: <>{l1}<br />{l2}</> },
    event.location && { k: "Where", v: <Lines text={event.location} /> },
    event.expected && { k: "Expected", v: event.expected },
  ].filter(Boolean);
  return (
    <div className="facts" style={{ "--n": facts.length }} data-testid="event-facts">
      {facts.map((f) => (
        <div key={f.k} data-testid={`event-fact-${f.k.toLowerCase()}`}><b>{f.k}</b><span>{f.v}</span></div>
      ))}
    </div>
  );
};

const FlyerDialog = ({ dlgRef, src, title }) => (
  <dialog ref={dlgRef} className="flyer-dlg" aria-label={`${title} flyer`} onClick={(e) => e.target === e.currentTarget && e.currentTarget.close()} data-testid="flyer-dialog">
    <img src={src} alt={`${title} flyer, full size`} />
    <button className="btn btn-gold" onClick={() => dlgRef.current.close()} data-testid="flyer-dialog-close-button">Close</button>
  </dialog>
);

export const EventItem = ({ event }) => {
  const dlgRef = useRef(null);
  const flyer = fileUrl(event.flyer_url);
  const external = (u) => /^https?:/i.test(u) ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <div className="event-block" data-testid={`event-item-${event.id}`}>
      <div className={`event ${flyer ? "" : "no-flyer"}`}>
        {flyer && (
          <button className="flyer" onClick={() => dlgRef.current?.showModal?.()} aria-label={`Enlarge the ${event.title} flyer`} data-testid="event-flyer-button">
            <img src={flyer} alt={`Flyer for the ${event.title}`} width="720" height="1080" />
            <span>Tap to enlarge</span>
          </button>
        )}
        <div>
          {event.tag && <div className="tag" data-testid="event-tag">{event.tag}</div>}
          <h3 data-testid="event-title">{event.title}</h3>
          {event.description && <p data-testid="event-description">{event.description}</p>}
          <Facts event={event} />
          {event.offerings?.length > 0 && (
            <ul className="offer" data-testid="event-offerings">{event.offerings.map((o) => <li key={o}>{o}</li>)}</ul>
          )}
          {event.partner_note && <p className="partner" data-testid="event-partner-note"><RichNote text={event.partner_note} /></p>}
          <div className="ctas">
            {event.directions_url && (
              <a className="btn btn-gold" href={event.directions_url} {...external(event.directions_url)} data-testid="event-directions-button">Get directions</a>
            )}
            {event.cta_url && (
              <a className="btn btn-ghost" href={event.cta_url} {...external(event.cta_url)} data-testid="event-cta-button">{event.cta_label || "Learn more"}</a>
            )}
          </div>
        </div>
      </div>
      {event.partners?.length > 0 && (
        <div className="partners" data-testid="event-partners">
          <h4>Confirmed community partners, vendors, and donors include</h4>
          <ul>{event.partners.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      )}
      {flyer && <FlyerDialog dlgRef={dlgRef} src={flyer} title={event.title} />}
    </div>
  );
};
